import { useEffect, useState } from "react";
import { COCKPIT_SECTION_GROUPS, COCKPIT_SECTIONS, type CockpitSectionId } from "./cockpitSections";

interface Props {
  resinLabel: string;
  monthLabel: string;
}

const NARROW_BAR_CLASSES =
  "max-[1000px]:top-[var(--cockpit-header-h,112px)] max-[1000px]:z-10 max-[1000px]:flex-row max-[1000px]:items-center max-[1000px]:gap-1 max-[1000px]:max-h-none max-[1000px]:overflow-x-auto max-[1000px]:py-2.5 max-[1000px]:bg-rs-black max-[1000px]:border-b max-[1000px]:border-rs-jet";

function scrollToSection(sectionId: CockpitSectionId) {
  document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
}

export function SectionMenu({ resinLabel, monthLabel }: Props) {
  const [currentSectionId, setCurrentSectionId] = useState<CockpitSectionId>(COCKPIT_SECTIONS[0].sectionId);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const enteringSection = entries.find((entry) => entry.isIntersecting);
        if (enteringSection) setCurrentSectionId(enteringSection.target.id as CockpitSectionId);
      },
      { rootMargin: "-25% 0px -65% 0px" },
    );
    for (const section of COCKPIT_SECTIONS) {
      const sectionElement = document.getElementById(section.sectionId);
      if (sectionElement) observer.observe(sectionElement);
    }
    return () => observer.disconnect();
  }, []);

  return (
    <nav
      aria-label="Cockpit sections"
      className={`sticky top-[calc(var(--cockpit-header-h,112px)+24px)] self-start flex flex-col gap-0.5 max-h-[calc(100vh-var(--cockpit-header-h,112px)-48px)] overflow-y-auto ${NARROW_BAR_CLASSES}`}
    >
      <div className="flex flex-col gap-0.5 p-3.5 mb-2.5 border border-rs-jet rounded-[10px] bg-rs-off-black max-[1000px]:hidden">
        <span className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-rs-light-gray/70">Working on</span>
        <b className="text-[15px] font-semibold text-white">{resinLabel}</b>
        <span className="text-[12.5px] text-rs-light-gray">{monthLabel}</span>
      </div>
      {COCKPIT_SECTION_GROUPS.map((group) => (
        <div key={group} className="contents">
          <p className="mx-3 mt-4 mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.1em] text-rs-light-gray/70 max-[1000px]:hidden">
            {group}
          </p>
          {COCKPIT_SECTIONS.filter((section) => section.group === group).map((section) => {
            const isCurrent = section.sectionId === currentSectionId;
            return (
              <button
                key={section.sectionId}
                type="button"
                aria-current={isCurrent ? "location" : undefined}
                className={`flex items-center gap-2.5 w-full text-left border-0 rounded-[7px] px-3 py-2 text-[13.5px] cursor-pointer max-[1000px]:w-auto max-[1000px]:flex-none max-[1000px]:whitespace-nowrap ${
                  isCurrent
                    ? "bg-rs-green/8 text-rs-green font-semibold"
                    : "bg-transparent text-rs-light-gray hover:bg-rs-off-black hover:text-white"
                }`}
                onClick={() => {
                  setCurrentSectionId(section.sectionId);
                  scrollToSection(section.sectionId);
                }}
              >
                <span
                  className={`material-symbols-sharp text-[18px] ${isCurrent ? "text-rs-green" : "text-rs-light-gray/60"}`}
                  aria-hidden="true"
                >
                  {section.icon}
                </span>
                {section.label}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
